"use client";

import { ExternalLink, X } from "lucide-react";
import {
	type CSSProperties,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { AffiliateImpression } from "@/components/affiliate/affiliate-impression";
import { AffiliateLink } from "@/components/affiliate/affiliate-link";
import { usePublishStickyCtaHeight } from "@/components/consent/sticky-cta-height";
import { offerGoSlug } from "@/lib/affiliate/go-link";
import type { AffiliatePlatform } from "@/lib/affiliate/links";
import { useAnalytics } from "@/lib/analytics/hooks";
import { trackMonetizationEvent } from "@/lib/analytics/track";
import type { Locale } from "@/lib/i18n/config";
import { accentPair } from "@/lib/monetization/accent";
import { wasSlotOutcomeRequested } from "@/lib/monetization/accounting-identity";
import { appendAdContextParams } from "@/lib/monetization/ad-context-client";
import { CONTEXT_BANDIT_FLAG } from "@/lib/monetization/context-bandit";
import { decisionTraceProps } from "@/lib/monetization/decision-trace";
import type { NormalizedOffer } from "@/lib/monetization/providers";
import {
	useStickyFooterClaimed,
	useStickyFooterStacked,
} from "@/lib/monetization/sticky-footer-claim";
import { useAdGuardrails } from "@/lib/monetization/use-ad-guardrails";
import { useSlotRequestTerminal } from "@/lib/monetization/use-slot-request-terminal";
import { RemoveAdsLink } from "./remove-ads-link";
import { SponsoredLabel } from "./sponsored-label";

/** Published by the bar while it is on screen, so a page can reserve room for
 * it (the Hunt composer spacer, the job Apply bar spacer, the desktop Hunt
 * shell) instead of guessing its height. `0px` whenever the bar is gone. */
export const AD_BAR_HEIGHT_VAR = "--ad-bar-h";

/**
 * Bottom sticky ad bar, all viewports (surface `mobile_sticky` — the name
 * predates the desktop bar; renaming the surface would orphan its history).
 * A COMPACT format — NativeAd's full card is too tall for a fixed bar —
 * sharing the same engine: fetches the engine-chosen offer, honors the
 * Pro/session-cap/dismiss guardrails, and carries the AffiliateImpression +
 * AffiliateLink tracking.
 *
 * Renders NOTHING until an offer actually resolves (no reserved space, no
 * skeleton) so it never adds CLS or shows an empty bar. Single row, ≤72px on
 * every viewport: Better Ads bans only LARGE sticky ads (>30% of the screen).
 *
 * The whole bar is one tap target: the CTA link carries the stretched-link
 * overlay (`after:absolute after:inset-0`), and the only other control, the
 * "Remove ads — Pro" link, sits above it at `z-10`. No nested `<a>`, no
 * click handler on a `div`. A close (X) renders only while the viewer's
 * segment has `adsDismissible` unset/true in the monetization payload.
 *
 * Page-owned sticky bars (see sticky-footer-claim.tsx):
 * - a BLOCKING claim (pricing CTA, pricing success) hides the bar entirely;
 * - a STACKABLE claim (job Apply bar, Hunt composer) keeps the bar, placed
 *   8px above that bar via the heights those bars publish, so the Apply button
 *   and the chat input are never covered.
 */
type State =
	| { status: "idle" }
	| { status: "offer"; offer: NormalizedOffer; market?: string }
	| { status: "empty" };

export function MobileStickyAd({
	locale,
	zone,
}: {
	locale: Locale;
	/** Page family the bar is on (job, jobs, blog, hunt, dashboard, …) —
	 * carried on `affiliate_impression` so coverage and CTR slice by page. */
	zone?: string;
}) {
	const l = locale === "en" ? "en" : "id";
	const [state, setState] = useState<State>({ status: "idle" });
	const [closed, setClosed] = useState(false);
	const ranRef = useRef(false);
	const barRef = useRef<HTMLDivElement>(null);
	const { posthog } = useAnalytics();
	const guard = useAdGuardrails("mobile_sticky");
	// Ref, not a dep: the fetch fires once guard is ready; a later segment
	// change must not rebuild `loadOffer`.
	const segmentRef = useRef(guard.segment);
	segmentRef.current = guard.segment;
	// Blocking claims only: a stackable claim (Apply bar, composer) keeps the
	// ad and moves it up instead — see `stacked`.
	const footerClaimed = useStickyFooterClaimed({ blockingOnly: true });
	const stacked = useStickyFooterStacked();
	// Honour a dismissal only while the payload still allows closing. With
	// `adsDismissible: false` an `ld_ad_dismissed` entry written earlier in
	// the session must not keep hiding the bar.
	const providerDismissed =
		state.status === "offer" &&
		guard.dismissible &&
		guard.isDismissed(state.offer.provider);

	// The old blindspot: a blocking claim hid the bar with no event at all, so
	// "the ad is missing on this page" was invisible. PostHog only, once per
	// mount, and only when the claim is the actual reason (a Pro or capped
	// viewer is reported by the ladder below). Never D1: the bar yields before
	// it requests, so there is no `requested` row for a terminal to close.
	const footerSuppressedRef = useRef(false);
	useEffect(() => {
		if (!footerClaimed || !guard.ready || guard.blocked) return;
		if (footerSuppressedRef.current) return;
		footerSuppressedRef.current = true;
		posthog?.capture("monetized_suppressed", {
			surface: "mobile_sticky",
			reason: "footer_claimed",
			zone,
		});
	}, [footerClaimed, guard.ready, guard.blocked, posthog, zone]);

	// Zero-blindspot accounting (PR 1, all-ads-affiliate-needs-atomic-moth).
	// This bar had NO suppression/empty tracking at all before this — every
	// non-fill outcome (blocked, empty, dismissed-in-a-prior-mount) was
	// completely silent in both PostHog and D1, on the surface that took 524
	// of 752 affiliate impressions in the week to 2026-09-07. Mirrors
	// `native-ad.tsx`'s ladder, including the same non-double-counting rule:
	// `closed` (this mount's own dismiss click) and `footerClaimed` (loadOffer
	// never even fires — see the effect below) are deliberately EXCLUDED,
	// because by the time either applies an impression has either already
	// fired (closed) or never should have (footerClaimed contributes zero to
	// `requested` too, so it owes nothing to the identity).
	const suppressedRef = useRef(false);
	const {
		begin,
		settle,
		awaitImpression,
		confirmImpression,
		markTerminal,
		canImpress,
	} = useSlotRequestTerminal(ranRef, "mobile_sticky", locale);
	const suppressionReason =
		!guard.ready || footerClaimed
			? null
			: guard.blocked
				? (guard.blockReason ?? "blocked")
				: state.status === "empty"
					? "empty"
					: providerDismissed
						? "dismissed"
						: null;
	useEffect(() => {
		if (!suppressionReason || suppressedRef.current) return;
		suppressedRef.current = true;
		// PostHog unaffected by the gate below — no "requested" counterpart to
		// reconcile against, so this has always fired for `blocked` regardless
		// of whether `loadOffer` ran.
		posthog?.capture("monetized_suppressed", {
			surface: "mobile_sticky",
			reason: suppressionReason,
		});
		// D1 must NOT emit a terminal outcome for a request that never
		// happened — CAUGHT IN REVIEW: the load effect below early-returns on
		// `guard.blocked` (line ~91), so for a Pro member `guard.blocked`
		// resolves true (`PRO_BLOCKED`, synchronous) and `loadOffer` NEVER
		// runs — deterministically, on every single Pro pageview. Without this
		// gate that fired `affiliate_slot_suppressed` with ZERO matching
		// `affiliate_slot_requested`, which would have paged the new p0 Sentry
		// alarm from ordinary Pro traffic on day one, not from a real
		// blindspot. wasSlotOutcomeRequested is the single gate this rule
		// lives in (accounting-identity.ts) — `ranRef.current` is the same
		// idempotence ref `loadOffer` itself uses.
		if (!wasSlotOutcomeRequested(suppressionReason, ranRef.current)) return;
		trackMonetizationEvent({
			eventKind:
				suppressionReason === "empty"
					? "affiliate_slot_empty"
					: "affiliate_slot_suppressed",
			surface: "mobile_sticky",
			locale,
		});
		// This request now has its terminal event; an unmount must not add another.
		markTerminal();
	}, [suppressionReason, locale, posthog, markTerminal]);

	// Zero-blindspot accounting, incident 2 (2026-09-23) — see
	// `useSlotRequestTerminal`'s doc comment. `suppressionReason === null` here
	// covers `!footerClaimed` too, so a bar yielded to a page-owned CTA never
	// arms this (it never left `"idle"`). Also covers the rare offer-with-no-
	// href case below for free: that path returns `null` without ever
	// mounting `AffiliateImpression`, so `confirmImpression` never runs and
	// `terminate()` still closes the identity on unmount.
	const impressionPending =
		guard.ready && state.status === "offer" && !suppressionReason;
	useEffect(() => {
		if (impressionPending) awaitImpression();
	}, [impressionPending, awaitImpression]);

	const loadOffer = useCallback(() => {
		if (ranRef.current) return;
		ranRef.current = true;
		// Zero-blindspot accounting (PR 1): every resolution ATTEMPT. Fired only
		// once guard.ready && !footerClaimed (see the effect below that calls
		// this), so a slot yielded to a page-owned CTA correctly contributes
		// zero to `requested` — it never asked.
		trackMonetizationEvent({
			eventKind: "affiliate_slot_requested",
			surface: "mobile_sticky",
			locale,
		});
		const params = new URLSearchParams({ surface: "mobile_sticky", locale });
		params.set("n", `${Date.now()}-${Math.floor(Math.random() * 1e9)}`);
		// Wave 4c targeting context + fatigue ledger (ignored server-side while
		// `engine.targeting.enabled` is false).
		appendAdContextParams(params, {
			segment: segmentRef.current,
			// 50/50 A/B (contextual bandit vs current engine). Read at fetch
			// time; an unresolved flag sends nothing and the route serves control.
			banditArm: posthog?.getFeatureFlag?.(CONTEXT_BANDIT_FLAG),
		});
		const signal = begin();
		fetch(`/api/monetization/offer?${params.toString()}`, {
			cache: "no-store",
			signal,
		})
			.then((r) => (r.ok ? r.json() : null))
			.then((data) => {
				// Aborted = unmounted / page hidden mid-fetch: the terminal event
				// was already emitted by useSlotRequestTerminal; never setState.
				if (!settle(signal)) return;
				const parsed = data as {
					offer?: NormalizedOffer | null;
					market?: string;
				} | null;
				const offer = parsed?.offer;
				setState(
					offer
						? { status: "offer", offer, market: parsed?.market }
						: { status: "empty" },
				);
			})
			.catch(() => {
				if (!settle(signal)) return;
				setState({ status: "empty" });
			});
	}, [locale, begin, settle, posthog]);

	// Fetch once the guardrail decision is known and not blocked (Pro / cap),
	// and only when no page-owned sticky CTA already claims the bottom slot.
	// This skips the request on every re-render/re-mount where the claim was
	// already in effect (e.g. client-side nav onto a page with a claiming CTA
	// while this component stays mounted from the layout). On a page's very
	// first commit, this component's effect and the claimant's `claim()` can
	// both fire in the same React flush before the claim's state update takes
	// effect — one fetch may still go out that render. That's harmless (never
	// shown, no impression recorded): the render guard below is what actually
	// guarantees the bar never covers the page's CTA, independent of timing.
	useEffect(() => {
		if (!guard.ready || guard.blocked || footerClaimed || ranRef.current)
			return;
		loadOffer();
	}, [guard.ready, guard.blocked, footerClaimed, loadOffer]);

	// The session budget is charged from `AffiliateImpression`'s 50%-visibility
	// observer (`onImpression` below), NOT from `state.status === "offer"`.
	// When the bar was `md:hidden` it was `display:none` on desktop, never
	// intersected, and never fired a real `affiliate_impression` — yet the old
	// effect still spent one of the two affiliate slots the session has, on
	// every public page, before any in-content slot had rendered. That is how a
	// bottom overlay bar came to hold 524 of 752 affiliate impressions in the
	// week to 2026-09-07 while `feed_inline` was 91% blank.

	const offer = state.status === "offer" ? state.offer : null;
	const href = offer ? (offer.href ?? offer.items?.[0]?.href) : undefined;
	const showing =
		!closed &&
		!guard.blocked &&
		!footerClaimed &&
		offer !== null &&
		!providerDismissed &&
		Boolean(href);
	usePublishStickyCtaHeight(barRef, showing, AD_BAR_HEIGHT_VAR);

	if (!showing || !offer || !href) return null;

	const platform = offer.provider as AffiliatePlatform | "educative";
	const pill = accentPair(offer.accent);

	const dismiss = () => {
		guard.dismiss(offer.provider);
		posthog?.capture("monetized_dismiss", {
			surface: "mobile_sticky",
			platform: offer.provider,
			locale: l,
		});
		setClosed(true);
	};

	// Stacked: 8px above the page-owned bar (Apply bar publishes
	// --sticky-cta-h, the Hunt composer --composer-h; only one is ever set on a
	// page). The gap keeps a thumb aimed at Apply/Send off the ad — invalid
	// clicks can get a partner account banned. Free-standing: the screen edge.
	const position = stacked
		? "bottom-[calc(var(--sticky-cta-h,0px)+var(--composer-h,0px)+0.5rem)]"
		: "bottom-0";

	return (
		<>
			{/* In-flow spacer so the last content and the footer links can scroll
			    clear of the fixed bar. Stacked claimants reserve the room
			    themselves (Apply bar, Hunt composer): a spacer here would lift a
			    sticky composer into the bar at the end of the page. */}
			{stacked ? null : (
				<div aria-hidden="true" className="h-[var(--ad-bar-h,0px)]" />
			)}
			<AffiliateImpression
				platform={offer.provider}
				surface="mobile_sticky"
				locale={l}
				extra={{
					market: state.status === "offer" ? state.market : undefined,
					decision_reason: offer.decision?.reason,
					...decisionTraceProps(offer.decision),
					stacked,
					zone,
				}}
				canFire={canImpress}
				onImpression={() => {
					guard.recordImpression();
					confirmImpression();
				}}
				className={`fixed inset-x-0 z-40 ${position}`}
			>
				<div
					ref={barRef}
					data-testid="sticky-ad-bar"
					className="relative border-t border-border bg-card/95 px-3 py-2 shadow-[0_-1px_8px_rgba(0,0,0,0.06)] backdrop-blur supports-[backdrop-filter]:bg-card/80"
					style={
						stacked
							? undefined
							: { paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }
					}
				>
					<div className="mx-auto flex max-w-3xl items-center gap-3">
						<span
							className="h-8 w-1 shrink-0 rounded-full"
							style={{ backgroundColor: offer.accent }}
							aria-hidden="true"
						/>
						<div className="min-w-0 flex-1">
							<div className="flex min-w-0 items-center gap-1.5">
								<p
									className="min-w-0 truncate text-xs font-semibold uppercase tracking-[0.1em]"
									style={{ color: offer.accent }}
								>
									{offer.eyebrow}
								</p>
								<SponsoredLabel
									locale={l}
									advertiseHref={`/${l}/advertise?src=mobile_sticky`}
								/>
								<RemoveAdsLink
									locale={l}
									surface="mobile_sticky"
									provider={offer.provider}
									className="ml-auto"
								/>
							</div>
							<p className="truncate text-xs font-semibold text-foreground">
								{offer.title}
							</p>
						</div>
						<AffiliateLink
							platform={platform}
							href={href}
							// The bar took 37 of 52 human affiliate clicks (30 d to
							// 2026-10-30) and linked the raw partner URL; the go-route
							// writes the click server-side and keeps crawlers off it.
							goSlug={offerGoSlug(offer)}
							surface="mobile_sticky"
							locale={l}
							extra={decisionTraceProps(offer.decision)}
							// Stretched link: the overlay covers the whole bar (the
							// `relative` strip above), so a tap anywhere on the ad opens
							// the partner through this one tracked anchor.
							className="inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-[background-color] after:absolute after:inset-0 after:content-[''] hover:[background-color:var(--offer-cta-hover)] active:[background-color:var(--offer-cta-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none"
							style={
								{
									backgroundColor: pill.bg,
									color: pill.fg,
									"--offer-cta-hover": pill.bgHover,
								} as CSSProperties
							}
						>
							{offer.cta}
							<ExternalLink className="h-3 w-3" aria-hidden="true" />
						</AffiliateLink>
						{guard.dismissible ? (
							<button
								type="button"
								onClick={dismiss}
								aria-label={l === "id" ? "Sembunyikan iklan" : "Dismiss ad"}
								className="relative z-10 -mr-1 shrink-0 rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
							>
								<X className="h-4 w-4" aria-hidden="true" />
							</button>
						) : null}
					</div>
				</div>
			</AffiliateImpression>
		</>
	);
}
